import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import LoadingInner from "@/components/Loading";
import NoData from "@/components/NoData";
import { BANG_KEO_PAGE_LIMIT, BANKER, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import LeagueFilterV21 from "../LeagueFilterV21";
import BookmakerSelect21 from "../BookmakerSelect21";
import KeoOddsMatchLeagues from "../KeoOddsMatchList";
import KeoOddsMatchRow from "../KeoOddsMatchList/KeoOddsMatchRow";
import LoadMoreButton from "@/components/LoadMoreButton";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

import CalendarIcon from "@/assets/images/v21-calendar.svg";
import SoccerBallIcon from "@/assets/images/v21-soccer-ball.svg";
import SortIcon from "@/assets/images/v21-sort.svg";

const KbvTiLeV21 = ({ type: initialType = "all" }) => {
    const { colorPrimary, colorSecondary, colorBg, colorText, colorForeground, hasDataConfig } = useCompConfig();
    const [selectedDate, setSelectedDate] = useState("");
    const [isHotOnly, setIsHotOnly] = useState(false);
    const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
    const [type, setType] = useState(initialType);
    const [activeStatus, setActiveStatus] = useState("all");

    const dispatch = useDispatch();
    const bookmarker = useSelector((state) => state.bookmakerReducer);

    const today = dayjs().tz(TZ);
    const todayString = today.format("YYYY-MM-DD");

    const dateOptions = useMemo(() => {
        const options = [];
        options.push({ value: "live", label: "Kèo trực tuyến", type: "live" });
        options.push({ value: today.format("YYYY-MM-DD"), label: "Hôm nay", type: "today" });
        const tomorrow = today.clone().add(1, "day");
        options.push({ value: tomorrow.format("YYYY-MM-DD"), label: "Ngày mai", type: "tomorrow" });
        for (let i = 2; i <= 5; i++) {
            const d = today.clone().add(i, "day");
            options.push({ value: d.format("YYYY-MM-DD"), label: d.format("DD/MM"), type: "future" });
        }
        return options;
    }, [todayString]);

    const dateString =
        !selectedDate || selectedDate === ""
            ? "all"
            : selectedDate === "live"
                ? todayString
                : selectedDate;

    const status = useMemo(() => {
        if (activeStatus === "live") return "live";
        if (activeStatus === "end") return "end";
        return type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";
    }, [type, selectedDate, activeStatus]);

    const queryObject = useMemo(
        () => ({
            include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
            filterInclude: `date:${dateString}`,
            filterOdds: -1,
            cache: false,
            status,
            page: 1,
            limit: BANG_KEO_PAGE_LIMIT,
        }),
        [dateString, status],
    );

    const {
        hotLeaguesWithMatches,
        leaguesWithMatches,
        loading,
        hasMore,
        loadMoreRef,
        isLoadingMore,
        handleLoadMore,
        total,
    } = useBangKeoMatches(queryObject);

    const baseLeaguesWithMatches = useMemo(
        () => (isHotOnly ? hotLeaguesWithMatches : leaguesWithMatches),
        [isHotOnly, hotLeaguesWithMatches, leaguesWithMatches],
    );

    const filteredLeaguesWithMatches = useMemo(() => {
        if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
        const idSet = new Set(selectedLeagueIds);
        return (baseLeaguesWithMatches || []).filter(({ league }) =>
            idSet.has(league.id),
        );
    }, [baseLeaguesWithMatches, selectedLeagueIds]);

    const matchIds = useMemo(
        () =>
            filteredLeaguesWithMatches.flatMap(
                (league) => league.matches?.map((match) => match.id) || [],
            ),
        [filteredLeaguesWithMatches],
    );

    const { clearAllData } = useLiveData();
    useLiveUpdates(matchIds);

    useEffect(() => {
        return () => {
            clearAllData();
        };
    }, [clearAllData]);

    useEffect(() => {
        setSelectedDate(todayString);
    }, [todayString]);

    const handleDateChange = (value) => {
        setSelectedDate(value);
        if (value === "live") {
            setActiveStatus("live");
        } else {
            setActiveStatus("all");
        }
    };

    const handleLeagueFilterChange = (ids) => {
        setSelectedLeagueIds(ids);
    };

    const filterRowRef = useRef(null);

    const themeStyle = hasDataConfig
        ? {
            "--theme-primary": colorPrimary,
            "--theme-highlight": colorSecondary,
            "--theme-bg": colorBg,
            "--theme-text": colorText,
            "--theme-foreground": colorForeground,
        }
        : undefined;

    const handleStatusClick = (s) => {
        setActiveStatus(s);
        if (s === "live") {
            setType("all");
            setSelectedDate("live");
        } else if (s === "end") {
            setType("ketqua");
        } else {
            setType("all");
            if (selectedDate === "live") setSelectedDate(todayString);
        }
    };

    return (
        <div
            className="mx-auto px-1 md:px-2 py-3 flex flex-col gap-5"
            style={themeStyle}
        >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-1 bg-[#F9FAFB] p-1 md:p-2 rounded-[12px] border border-[#F3F4F6]">
                <div className="grid grid-cols-2 lg:flex items-center gap-2 lg:gap-3 w-full lg:w-auto">
                    {/* Date Select */}
                    <BookmakerSelect21
                        className="w-full lg:w-auto"
                        value={selectedDate}
                        onChange={handleDateChange}
                        options={dateOptions}
                        placeholder="Chọn ngày"
                        icon={<img src={CalendarIcon} alt="calendar" className="w-[18px] h-[18px]" />}
                    />

                    {/* League Filter */}
                    <LeagueFilterV21
                        className="w-full lg:w-auto"
                        anchorRef={filterRowRef}
                        selectedIds={selectedLeagueIds}
                        onChange={handleLeagueFilterChange}
                    />
                </div>

                <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto pb-1 md:pb-0 no-scrollbar">
                    <button
                        onClick={() => handleStatusClick("live")}
                        className={`flex-1 lg:flex-none min-w-0 flex items-center justify-center gap-1.5 px-2 py-2 md:px-4 md:py-[10px] rounded-[6px] border [border-style:solid] transition-all font-bold text-[10px] md:text-[12px] uppercase whitespace-nowrap ${activeStatus === "live"
                            ? "bg-[#FFF1F2] border-[#FDA4AF] text-[#E11D48] shadow-sm"
                            : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-gray-300"
                            }`}
                    >
                        <img src={SoccerBallIcon} alt="live" className="w-3.5 h-3.5 md:w-[18px] md:h-[18px] shrink-0" />
                        <span className="truncate lg:overflow-visible">ĐANG ĐÁ {activeStatus === 'live' && total ? `(${total})` : ''}</span>
                    </button>

                    <button
                        onClick={() => handleStatusClick("all")}
                        className={`flex-1 lg:flex-none min-w-0 flex items-center justify-center gap-1.5 px-2 py-2 md:px-4 md:py-[10px] rounded-[6px] border [border-style:solid] transition-all font-bold text-[10px] md:text-[12px] uppercase whitespace-nowrap ${activeStatus === "all"
                            ? "bg-white border-blue-500 text-blue-600 shadow-sm"
                            : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-gray-300"
                            }`}
                    >
                        <img src={SortIcon} alt="sort" className="w-3 h-3 md:w-[14px] md:h-[14px] opacity-80 shrink-0" />
                        <span className="truncate lg:overflow-visible">SẮP ĐÁ {activeStatus === 'all' && total ? `(${total})` : ''}</span>
                    </button>

                    <button
                        onClick={() => handleStatusClick("end")}
                        className={`flex-1 lg:flex-none min-w-0 flex items-center justify-center gap-1.5 px-2 py-2 md:px-4 md:py-[10px] rounded-[6px] border [border-style:solid] transition-all font-bold text-[10px] md:text-[12px] uppercase whitespace-nowrap ${activeStatus === "end"
                            ? "bg-white border-gray-900 text-gray-900 shadow-sm"
                            : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-gray-300"
                            }`}
                    >
                        <img src={SortIcon} alt="sort" className="w-3 h-3 md:w-[14px] md:h-[14px] opacity-80 shrink-0" />
                        <span className="truncate lg:overflow-visible">ĐÃ KẾT THÚC {activeStatus === 'end' && total ? `(${total})` : ''}</span>
                    </button>
                </div>
            </div>

            {/* Danh sách trận đấu */}
            <div ref={filterRowRef}>
                {loading && <LoadingInner />}
                {!loading && (
                    <>
                        <div className="space-y-0">
                            {filteredLeaguesWithMatches.length === 0 ? (
                                <NoData message="Không có trận đấu nào" />
                            ) : (
                                filteredLeaguesWithMatches.map(({ league, matches }, index) => (
                                    <KeoOddsMatchLeagues
                                        key={league.id}
                                        league={league}
                                        matches={matches}
                                        isFirst={index === 0}
                                        render={(match, matchIndex) => (
                                            <KeoOddsMatchRow
                                                key={`${match.id}-${bookmarker}`}
                                                match={match}
                                                matchIndex={matchIndex}
                                            />
                                        )}
                                    />
                                ))
                            )}
                        </div>
                        <LoadMoreButton
                            isLoading={isLoadingMore}
                            onClick={handleLoadMore}
                            loadingComponent={<LoadingInner />}
                            hasMore={hasMore && !isHotOnly}
                            text="hiển thị thêm giải đấu"
                            className="font-bold uppercase text-[12px]"
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default KbvTiLeV21;
